import {
  hasActiveRunFilters,
  isRunStatusOption,
  isTriggerSourceFilter,
  normalizeSearchTerm,
  resolveRequestedSort,
  resolveSortQuery,
  resolveStatusQuery,
  resolveTriggeredRange,
  type RunListSort,
  type RunListSortDirection,
  type RunStatusOption,
  type TriggerSourceFilter,
} from './workflowRunQuery';

export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];

export function parsePageSizeOption(
  value: string | number | undefined,
): PageSizeOption | undefined {
  const parsedValue = typeof value === 'string' ? Number(value) : value;
  return PAGE_SIZE_OPTIONS.find((size) => size === parsedValue);
}

export type WorkflowRunFiltersStore = {
  statuses: RunStatusOption[];
  triggerSourceFilter: TriggerSourceFilter;
  triggeredFrom: string;
  triggeredTo: string;
  testCaseIds: string[];
  sort: RunListSort;
  pageSize: PageSizeOption;
  page: number;
  searchTerm: string;
  debouncedSearchTerm: string;
  setStatuses: (statuses: RunStatusOption[]) => void;
  setTriggerSourceFilter: (value: TriggerSourceFilter) => void;
  setTriggeredFrom: (value: string) => void;
  setTriggeredTo: (value: string) => void;
  setTestCaseIds: (ids: string[]) => void;
  setSort: (sort: RunListSort) => void;
  setPageSize: (pageSize: PageSizeOption) => void;
  setPage: (page: number, options?: { replace?: boolean }) => void;
  setSearchTerm: (value: string) => void;
  clearFilters: () => void;
};

/**
 * Shared filter/query view for the run lists. Local-state and URL-backed
 * stores both feed this so the table/filter bar stay on one API.
 */
export function createWorkflowRunFiltersApi(store: WorkflowRunFiltersStore) {
  const range = resolveTriggeredRange(store.triggeredFrom, store.triggeredTo);
  const statusQuery = resolveStatusQuery(store.statuses);
  const sortQuery = resolveSortQuery(store.sort);

  const withPageReset = <T>(setter: (value: T) => void) => {
    return (value: T) => {
      setter(value);
      store.setPage(1, { replace: true });
    };
  };

  return {
    statuses: store.statuses,
    statusFilter: statusQuery.statusFilter,
    triggerSourceFilter: store.triggerSourceFilter,
    testCaseIds: store.testCaseIds,
    searchTerm: store.searchTerm,
    triggeredFrom: store.triggeredFrom,
    triggeredTo: store.triggeredTo,
    sortBy: store.sort.sortBy,
    sortDirection: store.sort.sortDirection,
    isDefaultSort: sortQuery.isDefaultSort,
    pageSize: store.pageSize,
    page: store.page,
    offset: (store.page - 1) * store.pageSize,
    hasActiveFilters: hasActiveRunFilters({
      statuses: store.statuses,
      triggerSourceFilter: store.triggerSourceFilter,
      searchTerm: store.searchTerm,
      triggeredFrom: store.triggeredFrom,
      triggeredTo: store.triggeredTo,
      testCaseIds: store.testCaseIds,
    }),
    hasInvalidDateRange: range.hasInvalidDateRange,
    queryTestCaseIds:
      store.testCaseIds.length === 0 ? undefined : store.testCaseIds,
    queryStatus: statusQuery.queryStatus,
    queryStatuses: statusQuery.queryStatuses,
    queryOnlyScheduledReruns: statusQuery.queryOnlyScheduledReruns,
    queryTriggeredByType:
      store.triggerSourceFilter === 'all'
        ? undefined
        : store.triggerSourceFilter,
    queryTriggeredFrom: range.queryTriggeredFrom,
    queryTriggeredTo: range.queryTriggeredTo,
    querySearch: normalizeSearchTerm(store.debouncedSearchTerm),
    querySortBy: sortQuery.querySortBy,
    querySortDirection: sortQuery.querySortDirection,
    onStatusesChange: withPageReset((values: readonly string[]) =>
      store.setStatuses(values.filter(isRunStatusOption)),
    ),
    onStatusFilterChange: withPageReset((value: string) => {
      if (value === 'all') store.setStatuses([]);
      else if (isRunStatusOption(value)) store.setStatuses([value]);
    }),
    onTriggerSourceFilterChange: withPageReset((value: string) => {
      if (isTriggerSourceFilter(value)) store.setTriggerSourceFilter(value);
    }),
    onTriggeredFromChange: withPageReset(store.setTriggeredFrom),
    onTriggeredToChange: withPageReset(store.setTriggeredTo),
    onTestCaseIdsChange: withPageReset((values: readonly string[]) =>
      store.setTestCaseIds([...values]),
    ),
    onSearchTermChange: withPageReset(store.setSearchTerm),
    onSortChange: (
      nextSortBy: string | undefined,
      nextDirection: RunListSortDirection | undefined,
    ) => {
      store.setSort(resolveRequestedSort(nextSortBy, nextDirection));
      store.setPage(1, { replace: true });
    },
    onPageSizeChange: (value: string) => {
      const sizeOption = parsePageSizeOption(value);
      if (sizeOption !== undefined) {
        store.setPageSize(sizeOption);
        store.setPage(1, { replace: true });
      }
    },
    setPage: store.setPage,
    clearFilters: store.clearFilters,
  };
}

export type WorkflowRunFiltersApi = ReturnType<
  typeof createWorkflowRunFiltersApi
>;
